import type { SessionItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, errorMessage, get } from "../../api";
import { Alert, Button, Empty, Loading, PageHeader, Panel, formatDateTime } from "../../components/ui";
import { useSession } from "../../session";

export function SessionsSettings() {
  const { me } = useSession();
  const qc = useQueryClient();
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: () => get<SessionItem[]>("/api/sessions"), refetchInterval: 60_000 });
  const end = useMutation({ mutationFn: (id: string) => del(`/api/sessions/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }) });
  const tz = me?.company.timezone;

  return (
    <div>
      <PageHeader title="Signed-in sessions" description="Who's signed in, and on what device. Ending a session signs that device out right away." />
      {end.error && (
        <div className="mb-4">
          <Alert>{errorMessage(end.error)}</Alert>
        </div>
      )}
      {sessions.isLoading ? (
        <Loading />
      ) : sessions.error ? (
        <Alert>{errorMessage(sessions.error)}</Alert>
      ) : !sessions.data?.length ? (
        <Panel>
          <Empty>Nobody is signed in.</Empty>
        </Panel>
      ) : (
        <Panel className="overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>Person</th>
                <th>Device</th>
                <th className="hidden sm:table-cell">Signed in</th>
                <th className="hidden md:table-cell">Last active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sessions.data.map((s) => (
                <tr key={s.id}>
                  <td className="font-medium">
                    {s.userName}
                    {s.current && <span className="ml-2 text-sm text-muted">(this device)</span>}
                  </td>
                  <td className="text-sm">
                    {s.device}
                    {s.ip && <div className="text-xs text-muted">{s.ip}</div>}
                  </td>
                  <td className="hidden text-sm text-ink-soft sm:table-cell">{formatDateTime(s.createdAt, tz)}</td>
                  <td className="hidden text-sm text-ink-soft md:table-cell">{formatDateTime(s.lastSeenAt, tz)}</td>
                  <td className="text-right">
                    {!s.current && (
                      <Button className="px-3 py-1.5" busy={end.isPending && end.variables === s.id} onClick={() => end.mutate(s.id)}>
                        Sign out
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
